// «Editar / solicitar cambio» de la Ficha 360 (ronda 1). La política es POR CAMPO y la decide el
// backend (GET /entidades/ficha/politica): lo operativo se aplica al guardar; lo comercial sensible
// queda PENDIENTE de un supervisor y el call center sigue trabajando. Solo viajan los campos que
// cambiaron, todos con el mismo motivo, en UNA solicitud.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { claves, useComunas, usePoliticaFicha } from '../api/consultas';
import type {
  CampoFicha,
  FichaEntidad,
  ListaPrecio,
  ResultadoCambios,
  Vendedor,
} from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { NOMBRE_CONDICION, entradaDecimal } from '../comun/formato';
import { NOMBRE_CAMPO } from './valoresFicha';

const CONDICIONES = ['contado', 'credito', 'cheque', 'transferencia', 'otro'] as const;

interface Props {
  empresaId: string;
  ficha: FichaEntidad;
  vendedores: Vendedor[];
  listas: ListaPrecio[];
  /** Campos con una solicitud PENDIENTE: no se piden de nuevo hasta que se decida. */
  pendientes: ReadonlySet<CampoFicha>;
  alCerrar: () => void;
  alListo: (r: ResultadoCambios) => void;
}

export function EditarFicha({
  empresaId,
  ficha,
  vendedores,
  listas,
  pendientes,
  alCerrar,
  alListo,
}: Props) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const politica = usePoliticaFicha(empresaId, ficha.entidad.id);
  const comunas = useComunas();
  const rel = ficha.empresas.find((e) => e.empresa_id === empresaId) ?? null;
  const tel =
    ficha.medios.find((m) => m.tipo === 'telefono' && m.principal) ??
    ficha.medios.find((m) => m.tipo === 'telefono') ??
    null;
  const correo =
    ficha.medios.find((m) => m.tipo === 'correo' && m.principal) ??
    ficha.medios.find((m) => m.tipo === 'correo') ??
    null;

  const inicial = useMemo(
    () => ({
      telefono: tel?.valor ?? '',
      email: correo?.valor ?? '',
      contactoNombre: '',
      contactoCargo: '',
      calle: '',
      numero: '',
      comuna: '',
      referencia: '',
      observaciones: rel?.observaciones ?? '',
      linea: rel?.linea_credito ? String(Number(rel.linea_credito)) : '',
      condicion: rel?.condicion_pago ?? 'contado',
      plazo: String(rel?.plazo_dias ?? 0),
      lista: rel?.lista_precio_id ?? '',
      vendedor: rel?.vendedor_id ?? '',
      bloqueado: !!rel?.bloqueado,
      motivoBloqueo: rel?.motivo_bloqueo ?? '',
    }),
    [tel, correo, rel],
  );
  const [f, setF] = useState(inicial);
  const [motivo, setMotivo] = useState('');
  const poner = (campos: Partial<typeof f>) => setF((x) => ({ ...x, ...campos }));

  const aplicacion = (campo: CampoFicha) =>
    politica.data?.find((p) => p.campo === campo)?.aplicacion ?? 'NO_DISPONIBLE';

  /** Solo los campos que CAMBIARON (y que el usuario puede tocar). */
  const cambios = useMemo(() => {
    const c: Array<{ campo: CampoFicha; valor: unknown }> = [];
    const agregar = (campo: CampoFicha, cambio: boolean, valor: unknown) => {
      if (cambio && aplicacion(campo) !== 'NO_DISPONIBLE' && !pendientes.has(campo))
        c.push({ campo, valor });
    };
    agregar('telefono', f.telefono.trim() !== inicial.telefono && !!f.telefono.trim(), {
      valor: f.telefono.trim(),
    });
    agregar('email', f.email.trim() !== inicial.email && !!f.email.trim(), {
      valor: f.email.trim(),
    });
    agregar('contacto', !!f.contactoNombre.trim(), {
      nombre: f.contactoNombre.trim(),
      ...(f.contactoCargo.trim() ? { cargo: f.contactoCargo.trim() } : {}),
    });
    agregar('direccion', !!f.calle.trim() && !!f.comuna, {
      calle: f.calle.trim(),
      ...(f.numero.trim() ? { numero: f.numero.trim() } : {}),
      comunaCodigo: f.comuna,
      ...(f.referencia.trim() ? { referencia: f.referencia.trim() } : {}),
    });
    agregar('observaciones', f.observaciones.trim() !== inicial.observaciones.trim(), {
      valor: f.observaciones.trim() || null,
    });
    agregar('linea_credito', f.linea.trim() !== inicial.linea, {
      valor: f.linea.trim() ? entradaDecimal(f.linea, 0) : null,
    });
    agregar('condicion_pago', f.condicion !== inicial.condicion || f.plazo !== inicial.plazo, {
      condicion: f.condicion,
      plazoDias: Number(f.plazo || 0),
    });
    agregar('lista_precio', f.lista !== inicial.lista && !!f.lista, { listaPrecioId: f.lista });
    agregar('vendedor', f.vendedor !== inicial.vendedor, { vendedorId: f.vendedor || null });
    agregar(
      'estado_comercial',
      f.bloqueado !== inicial.bloqueado ||
        (f.bloqueado && f.motivoBloqueo.trim() !== inicial.motivoBloqueo),
      { bloqueado: f.bloqueado, ...(f.bloqueado ? { motivo: f.motivoBloqueo.trim() } : {}) },
    );
    return c;
  }, [f, inicial, politica.data, pendientes]);

  const pide = cambios.filter((c) => aplicacion(c.campo) === 'SOLICITUD');
  const directos = cambios.filter((c) => aplicacion(c.campo) === 'DIRECTA');
  const problemas = [
    ...(f.linea.trim() && !entradaDecimal(f.linea, 0) ? ['La línea es un monto en pesos'] : []),
    ...(!/^\d{1,3}$/.test(f.plazo || '0') ? ['El plazo va en días (0 a 365)'] : []),
    ...(f.bloqueado && f.motivoBloqueo.trim().length < 3 ? ['Bloquear exige motivo'] : []),
    ...(f.calle.trim() && !f.comuna ? ['La dirección nueva necesita su comuna'] : []),
    // Se avisa aquí y no en la firma (31-10: la solicitud entera cayó al aprobarla).
    ...(f.linea.trim() && f.condicion === 'contado'
      ? [
          'Con «Efectivo / transferencia» no hay línea de crédito: cambia la condición a crédito o cheque, o deja la línea vacía',
        ]
      : []),
  ];

  const enviar = useMutation({
    mutationFn: () => {
      // Sin motivo obligatorio (Rodrigo: trazabilidad, no motivos); el backend pide 3 letras.
      const cuerpo = {
        empresaId,
        motivo: motivo.trim().length >= 3 ? motivo.trim() : 'Edición de la ficha del cliente',
        campos: cambios,
      };
      return api.post<ResultadoCambios>(`/entidades/${ficha.entidad.id}/cambios`, cuerpo, {
        idempotencia: intentos.clave('cambios-ficha', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: claves.cliente(empresaId, ficha.entidad.id) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'solicitudes-cambio'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'contexto-comercial'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'politica-ficha'] }),
      ]);
      alListo(r);
    },
    onError: (e) => intentos.cerrar(e),
  });

  const listo = cambios.length > 0 && !problemas.length;

  const marca = (campo: CampoFicha) => {
    if (pendientes.has(campo)) return <span className="insignia tono-aviso">cambio pendiente</span>;
    const a = aplicacion(campo);
    return a === 'DIRECTA' ? (
      <span className="insignia tono-ok" title="Se aplica al guardar (queda en la auditoría)">
        directo
      </span>
    ) : a === 'SOLICITUD' ? (
      <span
        className="insignia tono-aviso"
        title="Queda pendiente hasta que un supervisor lo apruebe; el valor actual no cambia"
      >
        requiere aprobación
      </span>
    ) : (
      <span className="insignia tono-neutro">sin permiso</span>
    );
  };
  const bloqueado = (campo: CampoFicha) =>
    aplicacion(campo) === 'NO_DISPONIBLE' || pendientes.has(campo) || enviar.isPending;

  return (
    <Modal
      titulo={`Editar / solicitar cambio · ${ficha.entidad.razon_social}`}
      ancho="ancho"
      alCerrar={alCerrar}
      pie={
        <>
          <span className="apagado">
            {cambios.length
              ? `${directos.length} directo(s) · ${pide.length} con aprobación`
              : 'Sin cambios'}
          </span>
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="primario"
            disabled={!listo || enviar.isPending}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending
              ? 'Guardando…'
              : pide.length && !directos.length
                ? 'Enviar solicitud'
                : pide.length
                  ? 'Guardar y enviar solicitud'
                  : 'Guardar'}
          </button>
        </>
      }
    >
      {politica.isError && <PanelError error={politica.error} />}
      <div className="editar-ficha">
        <fieldset>
          <legend title="Son de la entidad: cambiarlos afecta a todas las empresas del grupo">
            Datos maestros · todo el grupo
          </legend>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.telefono} {marca('telefono')}
            </span>
            <input
              aria-label="Teléfono"
              data-autofoco
              value={f.telefono}
              disabled={bloqueado('telefono')}
              onChange={(e) => poner({ telefono: e.target.value })}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.email} {marca('email')}
            </span>
            <input
              aria-label="Correo"
              type="email"
              value={f.email}
              disabled={bloqueado('email')}
              onChange={(e) => poner({ email: e.target.value.toLowerCase() })}
            />
          </label>
          <div className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.contacto} {marca('contacto')}
            </span>
            <div className="fila-campos">
              <input
                aria-label="Nombre del contacto"
                placeholder="Nombre"
                value={f.contactoNombre}
                disabled={bloqueado('contacto')}
                onChange={(e) => poner({ contactoNombre: e.target.value.toUpperCase() })}
              />
              <input
                aria-label="Cargo del contacto"
                placeholder="Cargo"
                value={f.contactoCargo}
                disabled={bloqueado('contacto')}
                onChange={(e) => poner({ contactoCargo: e.target.value.toUpperCase() })}
              />
            </div>
          </div>
          <div className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.direccion} (de despacho) {marca('direccion')}
            </span>
            <div className="fila-campos">
              <input
                aria-label="Calle"
                placeholder="Calle"
                value={f.calle}
                disabled={bloqueado('direccion')}
                onChange={(e) => poner({ calle: e.target.value.toUpperCase() })}
              />
              <input
                aria-label="Número"
                placeholder="N°"
                className="corto"
                value={f.numero}
                disabled={bloqueado('direccion')}
                onChange={(e) => poner({ numero: e.target.value.toUpperCase() })}
              />
              <select
                aria-label="Comuna"
                value={f.comuna}
                disabled={bloqueado('direccion')}
                onChange={(e) => poner({ comuna: e.target.value })}
              >
                <option value="">Comuna…</option>
                {(comunas.data ?? []).map((c) => (
                  <option key={c.codigo} value={c.codigo}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
            <input
              aria-label="Referencia"
              placeholder="Referencia (opcional)"
              value={f.referencia}
              disabled={bloqueado('direccion')}
              onChange={(e) => poner({ referencia: e.target.value.toUpperCase() })}
            />
          </div>
        </fieldset>
        <fieldset>
          <legend>Relación comercial con esta empresa</legend>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.observaciones} {marca('observaciones')}
            </span>
            <textarea
              aria-label="Observaciones"
              rows={2}
              value={f.observaciones}
              disabled={bloqueado('observaciones')}
              onChange={(e) => poner({ observaciones: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.linea_credito} {marca('linea_credito')}
            </span>
            <input
              aria-label="Línea de crédito"
              inputMode="numeric"
              placeholder="sin línea"
              value={f.linea}
              disabled={bloqueado('linea_credito')}
              onChange={(e) => poner({ linea: e.target.value })}
            />
          </label>
          <div className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.condicion_pago} · plazo máximo {marca('condicion_pago')}
            </span>
            <div className="fila-campos">
              <select
                aria-label="Condición de pago"
                value={f.condicion}
                disabled={bloqueado('condicion_pago')}
                onChange={(e) => poner({ condicion: e.target.value })}
              >
                {CONDICIONES.map((c) => (
                  <option key={c} value={c}>
                    {NOMBRE_CONDICION[c] ?? c}
                  </option>
                ))}
              </select>
              <input
                aria-label="Plazo en días"
                className="corto"
                inputMode="numeric"
                value={f.plazo}
                disabled={bloqueado('condicion_pago')}
                onChange={(e) => poner({ plazo: e.target.value })}
              />
              <span className="apagado">días</span>
            </div>
          </div>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.lista_precio} {marca('lista_precio')}
            </span>
            <select
              aria-label="Lista de precios del cliente"
              value={f.lista}
              disabled={bloqueado('lista_precio')}
              onChange={(e) => poner({ lista: e.target.value })}
            >
              {!f.lista && <option value="">sin lista</option>}
              {listas
                .filter((l) => l.uso === 'venta')
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.codigo} {l.nombre}
                  </option>
                ))}
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.vendedor} {marca('vendedor')}
            </span>
            <select
              aria-label="Vendedor del cliente"
              value={f.vendedor}
              disabled={bloqueado('vendedor')}
              onChange={(e) => poner({ vendedor: e.target.value })}
            >
              <option value="">sin vendedor</option>
              {vendedores
                .filter((v) => v.activo || v.id === f.vendedor)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.codigo} · {v.nombre}
                  </option>
                ))}
            </select>
          </label>
          <div className="campo">
            <span className="etiqueta">
              {NOMBRE_CAMPO.estado_comercial} {marca('estado_comercial')}
            </span>
            <div className="fila-campos">
              <select
                aria-label="Estado comercial"
                value={f.bloqueado ? 'bloqueado' : 'activo'}
                disabled={bloqueado('estado_comercial')}
                onChange={(e) => poner({ bloqueado: e.target.value === 'bloqueado' })}
              >
                <option value="activo">Activo</option>
                <option value="bloqueado">Bloqueado</option>
              </select>
              {f.bloqueado && (
                <input
                  aria-label="Motivo del bloqueo"
                  placeholder="Motivo del bloqueo"
                  value={f.motivoBloqueo}
                  disabled={bloqueado('estado_comercial')}
                  onChange={(e) => poner({ motivoBloqueo: e.target.value })}
                />
              )}
            </div>
          </div>
        </fieldset>
      </div>
      <label className="campo">
        <span className="etiqueta">Comentario (opcional, lo ve quien autoriza)</span>
        <input
          aria-label="Motivo del cambio"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      {problemas.length > 0 && (
        <ul className="problemas" role="alert">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {enviar.isError && <PanelError error={enviar.error} />}
    </Modal>
  );
}
